'use client';

import { CatSearchForm } from '@blocks-react/components/SearchForm';
import { CatIconSearch } from '@blocks-react/components/Icons/Search';
import { useEffect, useRef, useState } from 'react';
import { BlocksSearchForm } from '@blocks-web-components/components/search-form/search-form';

interface DealerLocatorSearchFormProps {
  isVisible?: boolean;
  children?: React.JSX.Element | React.JSX.Element[];
  value: string;
  placeholder?: string;
  onSearch: (string: string) => void;
  onKeyPress: (string: string) => void;
  onCancel: () => void;
  mobileBreakPoint?: number;
  isInputError?: boolean;
  setIsInputError: (arg?: boolean) => void;
  isSearchLoading?: boolean;
  locationSelected?: string;
  isDisabled?: boolean;
}

export const DealerLocatorSearchForm = ({
  isVisible,
  children,
  value,
  placeholder,
  onSearch,
  onKeyPress,
  onCancel,
  mobileBreakPoint,
  isInputError,
  setIsInputError,
  isSearchLoading,
  locationSelected,
  isDisabled
}: DealerLocatorSearchFormProps) => {
  const [hasFocusedInput, setHasFocusedInput] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const ref = useRef<BlocksSearchForm>(null);

  useEffect(() => {
    if (isVisible && !hasFocusedInput) {
      setIsActive(false);
      setHasFocusedInput(true);
      setTimeout(() => {
        ref?.current?.focus();
        if (locationSelected) {
          // Auto-search for current selected store when the drawer is opened
          onSearch(locationSelected);
        }
      }, 300);
    }
  }, [isVisible, onSearch, locationSelected, hasFocusedInput]);

  useEffect(() => {
    if (!isVisible) {
      setHasFocusedInput(false);
    }
  }, [isVisible]);

  const handleSearch = (searchText: string) => {
    ref.current?.blur();
    setIsActive(false);
    onSearch(searchText);
  };

  const onFocus = () => {
    if (isInputError && document.activeElement === ref.current) {
      setIsInputError(false);
    }
  };

  const handleChange = (searchText: string | null) => {
    if (searchText === null) {
      // User clicked on X button
      if (!isSearchLoading) {
        onCancel();
      } else {
        // Don't allow input clearance if results are loading
        onKeyPress(value);
      }
      setIsActive(false);
    } else {
      onKeyPress(searchText);
      setIsActive(!!searchText);
    }
  };

  return (
    <CatSearchForm
      ref={ref}
      onClick={onFocus}
      isActive={isActive}
      onBlSearch={(e: Event) =>
        handleSearch((e.target as HTMLTextAreaElement).value)
      }
      onBlChange={(e) => handleChange(e.detail.value)}
      onBlSelect={(e: Event) =>
        handleSearch((e.target as HTMLTextAreaElement).value)
      }
      onKeyUp={(e) =>
        e.key === 'Enter' &&
        handleSearch((e.target as HTMLTextAreaElement).value)
      }
      placeholder={placeholder}
      status={isInputError ? 'error' : undefined}
      mobileBreakPoint={mobileBreakPoint}
      data-testid="dealer-locator-search-form"
      value={value}
      isInline
      position="bottom-start"
      disabled={isDisabled}
    >
      <CatIconSearch slot="before" />
      {children}
    </CatSearchForm>
  );
};
